import { useState } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db6fno3ghk82orf0cl4g";

type Status = "idle" | "submitting" | "success" | "error";

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = ((formData.get("contact_alt") as string) || "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "contact_alt") return;
      payload.append(key, value as string);
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const code = parsed?.code;
      const isSpam =
        typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          isSpam
            ? "提交内容被识别为垃圾信息，请修改后重试。"
            : serverMsg || "提交失败，请稍后重试。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试。");
    }
  };

  return (
    <div className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-8">
      <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-2">意见反馈</h3>
      <p className="text-sm text-foreground-600 mb-6 leading-relaxed">
        对本站内容有建议或想补充资讯？欢迎留言告诉我们。
      </p>

      {status === "success" ? (
        <div className="flex flex-col items-center text-center py-10">
          <span className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-2xl mb-4">
            <i className="ri-check-line"></i>
          </span>
          <p className="text-base font-medium text-foreground-950 mb-2">提交成功，感谢你的反馈！</p>
          <p className="text-sm text-foreground-500 mb-5">我们会认真阅读每一条留言。</p>
          <button
            type="button"
            onClick={() => setStatus("idle")}
            className="px-5 py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            再写一条
          </button>
        </div>
      ) : (
        <form
          className="feedback-form flex flex-col gap-4"
          data-readdy-form
          onSubmit={handleSubmit}
        >
          <div className="flex flex-col gap-1.5">
            <label htmlFor="fb-name" className="text-sm font-medium text-foreground-800">
              昵称
            </label>
            <input
              id="fb-name"
              name="name"
              type="text"
              required
              placeholder="请输入您的昵称"
              className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="fb-email" className="text-sm font-medium text-foreground-800">
              邮箱
            </label>
            <input
              id="fb-email"
              name="email"
              type="email"
              required
              placeholder="请输入您的邮箱"
              className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="fb-message" className="text-sm font-medium text-foreground-800">
              留言内容
            </label>
            <textarea
              id="fb-message"
              name="message"
              required
              maxLength={500}
              rows={4}
              placeholder="请输入您的建议或反馈（最多 500 字）"
              className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"
            ></textarea>
          </div>

          <input
            type="text"
            name="contact_alt"
            className="feedback-extra-field"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
          />

          {status === "error" && (
            <p className="text-sm text-accent-700 bg-accent-50 rounded-md px-3.5 py-2.5">
              {formError}
            </p>
          )}

          <button
            type="submit"
            disabled={status === "submitting"}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {status === "submitting" ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                提交中...
              </>
            ) : (
              <>
                <i className="ri-send-plane-line"></i>
                提交反馈
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}